// src/components/SuccessAlert.tsx

import React, { useState, useEffect } from "react";
import Lottie from "lottie-react";
import successAnimation from "../../assets/animations/successAnimation.json";
import { Button, Typography, CircularProgress, Box } from "@mui/material";

import "./SuccessAlert.css";
import { usePremiumStatus } from "../../context/PremiumStatusContext";
import { RoleDisplayingStripe } from "../../data/data";

interface SuccessAlertProps {
  message: string;
  secondMessage: boolean;
  onClose: () => void;
}

const SuccessAlert: React.FC<SuccessAlertProps> = ({
  message,
  secondMessage,
  onClose,
}) => {
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);
  const { currentRoleStripe, subscriptionTypeStripe, isLoading } =
    usePremiumStatus();

  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent;
      setIsMobile(/Mobi|Android|iPhone|iPad|iPod/i.test(ua));
    };

    checkMobile();
  }, []);

  const handleRedirectToApp = () => {
    const appDeepLink = "com.seagoapp.app:/?status=success";
    window.location.href = appDeepLink;
  };

  const handleAnimationComplete = () => {
    setIsAnimationPlaying(false);
  };

  return (
    <div className="alert-container" role="alert" aria-live="assertive">
      <Box className="alert-box">
        <div className="content-wrapper">
          <Lottie
            animationData={successAnimation}
            loop={false}
            autoplay={true}
            className="lottie-animation"
            onComplete={handleAnimationComplete}
          />
        </div>

        <Box padding="1.5rem">
          <Typography className="alert-message" gutterBottom>
            <div className="font-styling">{message}</div>
          </Typography>

          <Typography
            variant="subtitle1"
            className="subscription-status"
            gutterBottom
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : currentRoleStripe && subscriptionTypeStripe ? (
              <div className="subscription-font-styling">
                Subscription plan: {RoleDisplayingStripe(currentRoleStripe)}{" "}
                {subscriptionTypeStripe}
              </div>
            ) : null}
          </Typography>

          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
            flexDirection="row"
            gap={2}
            flexWrap="wrap"
          >
            <Button
              onClick={onClose}
              variant={!isMobile ? "contained" : "outlined"}
              color="primary"
              size="large"
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: "bold",
              }}
              disabled={isAnimationPlaying}
              fullWidth={!isMobile}
            >
              {isLoading && isMobile ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  &nbsp; Loading...
                </>
              ) : !isMobile ? (
                "OK!"
              ) : (
                "Close"
              )}
            </Button>

            {isMobile && (
              <Button
                onClick={handleRedirectToApp}
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  fontWeight: "bold",
                  mt: 0,
                }}
                disabled={isAnimationPlaying}
              >
                Open App
              </Button>
            )}
          </Box>
          {secondMessage && (
            <Typography
              variant="body2"
              color="textSecondary"
              className="open-app-message"
              sx={{
                mt: 1,
              }}
            >
              Please open the app to set up your{" "}
              {RoleDisplayingStripe(currentRoleStripe)} profile.
            </Typography>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default SuccessAlert;
